import type { Dateable } from 'pkg/api/models/dateable';
import type { FormEvent } from 'pkg/api/models/form_event';
import type { Group } from 'pkg/api/models/group';
import type { ProductPrice } from 'pkg/api/models/product_price';
import type { PublicModel } from 'pkg/api/models/public';
import type { Record } from 'pkg/api/models/record';
import type { SoftDeletable } from 'pkg/api/models/soft_deletable';
import type { User, UserSex } from 'pkg/api/models/user';

/**
 * The upsert payload types for `FormEventGroups.Upsert`.
 *
 * The endpoint is a full-state replace over the set of groups: every group must
 * be echoed or it gets deleted. Shared by the producers of this payload (the
 * event group editor and the tryout setup flow) so the two can't drift.
 */
export interface EventUpsertParam {
	/** Client-minted UUID matching the session across saves; becomes FormEvent.key. */
	key: string;
	startsAt: number;
	endsAt: number;
}

export enum PricingType {
	Fixed = 'fixed',
	PerEvent = 'per_event',
}

export enum Status {
	Unscheduled = 'unscheduled',
	Upcoming = 'upcoming',
	Ongoing = 'ongoing',
	Closed = 'closed',
	Canceled = 'canceled',
}

export interface Availability {
	status: Status;
	full: boolean;
	selectable: boolean;
	waitlistJoinable?: boolean;
	// Optional so an app deployed ahead of the API still renders every program.
	finished?: boolean;
	openSpots?: number;
}

export interface PriceUpsertParam {
	cost: number | null;
	taxRateId: number | null;
	pricingType?: PricingType;
}

export interface EventGroupUpsertParam {
	id?: number;
	name: string;
	// Inherited by the group's calendar events; upcoming events follow changes.
	location?: string;
	// Inherited by the group's calendar events; every event follows changes.
	description?: string;
	// Inherited by the group's calendar events; upcoming events follow changes.
	meetBeforeMinutes?: number;
	// Inherited by the group's calendar events; every event follows changes.
	hideParticipants?: boolean;
	// Inherited by the group's calendar events; every event follows changes.
	isPrivate?: boolean;
	// Inherited by the group's calendar events; every event follows changes.
	// Omitted = false, so producers that want the default (shown on website)
	// must send true explicitly.
	isPublic?: boolean;
	// Inherited by the group's calendar events; upcoming events follow changes.
	// Null = uncapped. A session's own capacity is read-only, so this is the
	// only place it can be set.
	capacity?: number | null;
	eligibleSex?: UserSex;
	eligibleBirthDateFrom?: string | null;
	eligibleBirthDateTo?: string | null;
	registrantsChooseDates?: boolean;
	waitlistEnabled?: boolean;
	groupId?: number;
	// Omitted = leave the group's pricing untouched; present = the desired state
	// (cost null = free). The server owns the backing product/price. Set-once:
	// the server rejects changes to a saved price, so locked groups omit it.
	price?: PriceUpsertParam;
	// Omitted = leave the group's events untouched; present = the desired set of
	// upcoming sessions (past events are server-protected either way).
	events?: EventUpsertParam[];
	// Omitted = leave the group's organizers untouched; present = the desired set.
	// Organizers are added to (and removed from) the group's upcoming events.
	organizerUserIds?: number[];
}

/**
 * The echo shape for groups the caller isn't editing. Unlike `price`, `events`
 * and `organizerUserIds`, the scalar fields of the upsert are always written,
 * so an echoed group must carry every one of them or the server blanks it —
 * and propagates the blank to the group's calendar events. `Required` pins the
 * contract at compile time: adding a scalar to EventGroupUpsertParam breaks
 * `toEventGroupEchoParam` until it copies the new field.
 */
export type EventGroupEchoParam = Required<
	Omit<
		EventGroupUpsertParam,
		| 'price'
		| 'events'
		| 'organizerUserIds'
		| 'groupId'
		| 'registrantsChooseDates'
		| 'waitlistEnabled'
	>
> &
	Pick<EventGroupUpsertParam, 'groupId'>;

/**
 * The fields the booking editor owns. Everything else on the stored group is
 * carried through untouched by `toEditedGroupParam`.
 */
export type EditedGroupFields = Pick<
	EventGroupUpsertParam,
	| 'name'
	| 'location'
	| 'description'
	| 'meetBeforeMinutes'
	| 'hideParticipants'
	| 'isPrivate'
	| 'isPublic'
	| 'capacity'
	| 'eligibleSex'
	| 'eligibleBirthDateFrom'
	| 'eligibleBirthDateTo'
	| 'registrantsChooseDates'
	| 'waitlistEnabled'
	| 'price'
	| 'events'
	| 'organizerUserIds'
>;

/**
 * The create branch of the editor's save handler builds this payload as a literal instead of
 * through `toEditedGroupParam` — there is no stored group to echo — so nothing else forces it
 * to carry every scalar the editor owns. `Required<>` pins the plain scalars the same way
 * `EventGroupEchoParam` pins the echo, for the same reason: `price`, `events` and
 * `organizerUserIds` are each computed conditionally and legitimately absent on create, while
 * every other field EditedGroupFields covers must be set explicitly. A scalar added to
 * EditedGroupFields breaks the create branch until it sets that scalar too.
 */
export type CreatedGroupFields = Required<
	Omit<
		EditedGroupFields,
		| 'price'
		| 'events'
		| 'organizerUserIds'
		| 'registrantsChooseDates'
		| 'waitlistEnabled'
	>
> &
	Pick<
		EventGroupUpsertParam,
		| 'price'
		| 'events'
		| 'organizerUserIds'
		| 'groupId'
		| 'registrantsChooseDates'
		| 'waitlistEnabled'
	>;

/**
 * The one set of waitlist figures the API serves for a program, recalculated on every read and
 * leaving out archived children (api/FORMS.md "Capacity"). Every surface reads these rather than
 * counting on its own, so they cannot disagree.
 */
export interface WaitlistFigures {
	waitlisted: number;
	invited: number;
	// Seated on the limiting session: the capped upcoming session with the fewest free spots.
	// Null without a capped upcoming session, as is free.
	booked: number | null;
	// That session's limit minus booked. Not floored: negative on a session seated past its limit.
	free: number | null;
	// Held for the waitlist, and open to everyone. Also null on a program without a waitlist.
	held: number | null;
	open: number | null;
}

export interface FormEventGroupOrganizer extends Record {
	formEventGroupId: number;
	userId: number;
	user?: User;
}

export interface FormEventGroup
	extends Record, SoftDeletable, Dateable, PublicModel {
	formId: number;
	name: string;
	location: string;
	description: string;
	meetBeforeMinutes: number;
	hideParticipants: boolean;
	isPrivate: boolean;
	isPublic: boolean;
	// The participant limit the booking pushes onto its upcoming sessions.
	// Null means uncapped. Only editable here, never on a session.
	capacity: number | null;
	// Computed, never persisted: the smallest remaining-seat count across the booking's capped
	// upcoming events, absent when it has none. Sent on admin and submission payloads alike.
	// Distinct from Form.spotsLeft, which counts submission slots on the whole registration
	// (AvailableSpots divides it by maxSubmissions) -- this one counts participant seats on an
	// event.
	spotsLeft?: number;
	// Computed, never persisted, admin payload only: the largest accepted-participant count
	// across the booking's upcoming, not-RSVP-expired events -- the floor a new limit cannot
	// go below without stranding someone already seated. Absent only when no event qualifies.
	minimumCapacity?: number;
	eligibleSex: UserSex;
	eligibleBirthDateFrom: string | null;
	eligibleBirthDateTo: string | null;
	registrantsChooseDates: boolean;
	waitlistEnabled: boolean;
	// Computed, never persisted, admin payload only: the program's waitlist figures, absent with the
	// waitlist flag off and on a program without a waitlist. See WaitlistFigures.
	waitlistFigures?: WaitlistFigures;
	hasWaitlistEntries?: boolean;
	pricingType: PricingType;
	// Computed, never persisted: whether a registrant can select the booking, from the backend's
	// FormEventGroup.PopulateAvailability. Absent on payloads that don't populate it.
	availability?: Availability;

	groupId?: number;
	group?: Group;

	productPriceId?: number;
	productPrice?: ProductPrice;

	events: FormEvent[];
	organizers?: FormEventGroupOrganizer[];
}
